/**
 * DOC HELPER — WRK-04 canvas controller boundary.
 *
 * This hook coordinates canvas projection actions and backend append requests.
 * It does not render UI, own canonical context state, or reinterpret saved
 * analysis content beyond forwarding explicit user edits to the session API.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createCoreGenerativeRegistry } from '@/chat/ui/index.js';
import { useCanvasProjection } from './projection.jsx';
import { useArtifacts } from '../../application/artifacts/provider';
import { usePatchQuestionSubmission } from '../../application/composer/usePatchQuestionSubmission';
import { projectPatchQuestionPresentations } from '../../projection/timeline/assistantTextSelector.js';
import { useSourcing } from '../../application/sourcing/provider';
import { resolveCanvasSourcingLoading } from './sourcing-ui-state.js';
import { resolveCitationNavigation } from '../../projection/canvas/savedRetrievalEvidenceProjection.js';

const choose = (condition, whenTrue, whenFalse) => (condition ? whenTrue() : whenFalse());

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

export function useCanvasController({
  projectId, userId, activeSessionId,
  sessionContext,
  timeline,
  composerModeKey,
  evidenceStrictMode,
  evidenceHitScoreThreshold,
  showCanvasPanel,
  setShowCanvasPanel,
  canvasActiveTabKey,
  setCanvasActiveTabKey,
  canvasFullscreenOpen,
  setCanvasFullscreenOpen,
  inputState,
  themeTokens,
  onPatchQuestionAnswerSubmit,
}) {
  const [canvasFocusEvidenceAnchorId, setCanvasFocusEvidenceAnchorId] = useState('');
  const { save: handleCanvasDraftSaved, status: saveStatus, selection, openArtifact, clearSelection, presentationContext } = useArtifacts();
  const scopeKey = JSON.stringify([projectId, userId, activeSessionId]);
  const { presentations, submit } = usePatchQuestionSubmission({ scopeKey, onSubmit: onPatchQuestionAnswerSubmit });
  const semanticMapRefreshPending = saveStatus.pending;
  const { timelineItems: resolvedTimelineItems, hasMore: sourcingHasMore,
    pending: sourcingLoadingMore, loadMore } = useSourcing();

  const handleUICardAction = useCallback((action) => {
    const actionType = String(firstTruthy(() => Object(action).type, () => Object(action).action_key, () => '')).trim();
    const actionHandlers = {
      submit_patch_question_answer: () => submit(action),
      cancel_patch_question_answer: () => submit(action),
      open_evidence_citation: async () => {
        const navigation = resolveCitationNavigation(action, resolvedTimelineItems, sessionContext?.artifact_tab_projection);
        if (!navigation) return;
        setShowCanvasPanel(true);
        if (!navigation.artifact_target) clearSelection();
        const selected = navigation.artifact_target ? await openArtifact(navigation) : navigation;
        if (!selected) return;
        setCanvasActiveTabKey(selected.activeTab);
        setCanvasFocusEvidenceAnchorId(navigation.anchor);
      },
      open_artifact_document: async () => {
        setShowCanvasPanel(true);
        setCanvasFocusEvidenceAnchorId('');
        const selected = await openArtifact(action);
        if (selected) setCanvasActiveTabKey(selected.activeTab);
      },
    };
    const actionHandler = actionHandlers[actionType];
    if (actionHandler) {
      return actionHandler();
    }
    if (firstTruthy(() => actionType.includes('canvas'), () => actionType.includes('evidence'), () => actionType.includes('sourcing'))) {
      setShowCanvasPanel(true);
      setCanvasActiveTabKey(choose(actionType.includes('sourcing'), () => ('sourcing'), () => ('evidence')));
    }
  }, [
    submit,
    setCanvasActiveTabKey,
    setShowCanvasPanel,
    openArtifact,
    resolvedTimelineItems,
    clearSelection,
    sessionContext,
  ]);

  const generativeRegistry = useMemo(() => {
    return createCoreGenerativeRegistry();
  }, []);

  useEffect(() => {
    if (!canvasFocusEvidenceAnchorId) {
      return;
    }
    const timer = setTimeout(() => setCanvasFocusEvidenceAnchorId(''), 1600);
    return () => clearTimeout(timer);
  }, [canvasFocusEvidenceAnchorId]);

  const handleCanvasSourcingLoadMore = useCallback(async () => {
    const result = await loadMore();
    if (result.status === 'completed') {
      setShowCanvasPanel(true);
      setCanvasActiveTabKey('sourcing');
    }
    return result;
  }, [loadMore, setCanvasActiveTabKey, setShowCanvasPanel]);

  const sourcingLoading = resolveCanvasSourcingLoading({
    timelineLoading: timeline?.loading,
    sourcingLoadingMore,
  });
  const semanticMapRefreshInFlight = semanticMapRefreshPending || Boolean(timeline?.loading);

  const { canvasAvailable, canvasDevDiagnostics, canvasPanelNode, renderedTimelineItems } = useCanvasProjection({
    timelineItems: resolvedTimelineItems,
    sessionContext: presentationContext ?? sessionContext,
    artifactReadState: selection,
    composerModeKey,
    evidenceStrictMode,
    evidenceHitScoreThreshold,
    showCanvasPanel,
    canvasFocusEvidenceAnchorId,
    setCanvasActiveTabKey,
    setShowCanvasPanel,
    canvasActiveTabKey,
    canvasFullscreenOpen,
    setCanvasFullscreenOpen,
    inputState,
    themeTokens,
    sourcingHasMore,
    sourcingLoading,
    sourcingLoadingMore,
    semanticMapRefreshPending: semanticMapRefreshInFlight,
    onCanvasSourcingLoadMore: handleCanvasSourcingLoadMore,
    onCanvasDraftSaved: handleCanvasDraftSaved,
  });

  return {
    canvasAvailable,
    canvasDevDiagnostics,
    canvasPanelNode,
    renderedTimelineItems: projectPatchQuestionPresentations(renderedTimelineItems, presentations, scopeKey),
    handleUICardAction,
    generativeRegistry,
  };
}

export default useCanvasController;
